<script lang="ts">
  import SvelteSeo from 'svelte-seo';
  import { createPageSEO, createWebPageSchema } from '$lib/config/seo';
  import { Button } from '$lib/components/ui/button';
  import { PUBLIC_EMAIL_LEGAL } from '$env/static/public';
	import { page } from '$app/stores';
  const seo = createPageSEO({
    title: 'Terms of Service - TalentProfile',
    description: 'Terms of service for using TalentProfile job matching platform.',
    canonical: '/terms',
    structuredData: createWebPageSchema({
      name: 'Terms of Service',
      description: 'TalentProfile terms of service and user agreement',
      url: '/terms'
    })
  });
</script>

<SvelteSeo {...seo} />

<div class="min-h-[calc(100dvh-527px)] bg-gradient-to-b from-white to-gray-50">
  <!-- Content -->
  <div class="container mx-auto px-4 py-20">
    <div class="max-w-4xl mx-auto">
      <!-- Hero -->
      <div class="text-center mb-16">
        <h1 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Terms of Service
        </h1>
        <p class="text-lg text-gray-600">
          Last updated: November 2025
        </p>
      </div>

      <!-- Content -->
      <div class="prose prose-lg max-w-none">
        <div class="bg-white rounded-lg border p-8 mb-8">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
          <p class="text-gray-600 leading-relaxed">
            By receiving and interacting with emails from Talent<strong>Profile</strong>, you acknowledge these
            Terms of Service. If you do not agree, you may unsubscribe or delete your data at any
            time through the preference link in our emails.
          </p>
        </div>

        <div class="space-y-8">
          <section class="bg-white rounded-lg border p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4">1. Service Description</h2>
            <p class="text-gray-600 leading-relaxed mb-4">
              Talent<strong>Profile</strong> is a job discovery service that:
            </p>
            <ul class="space-y-2 text-gray-600">
              <li>
                • Collects publicly available developer profiles from platforms like GitHub and
                LinkedIn
              </li>
              <li>• Analyzes your skills and experience</li>
              <li>• Matches you with relevant job opportunities</li>
              <li>• Sends personalized job alerts via email</li>
              <li>• Provides links to job postings on external websites</li>
            </ul>
            <p class="text-gray-600 leading-relaxed mt-4">
              <strong>Important:</strong> Talent<strong>Profile</strong> is a matching service only. We do not employ
              candidates, post jobs, or control hiring. All employment relationships are between you
              and hiring companies.
            </p>
          </section>

          <section class="bg-white rounded-lg border p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4">2. No Registration Required</h2>
            <p class="text-gray-600 leading-relaxed mb-4">
              <strong>You did not sign up for TalentProfile.</strong> We discovered your publicly available
              profile and determined you might be interested in tech job opportunities.
            </p>
            <p class="text-gray-600 leading-relaxed">
              We collect only information that you have made publicly available. By publishing your
              profile on platforms like GitHub or LinkedIn, you have made this information
              accessible to anyone, including job matching services like ours.
            </p>
          </section>

          <section class="bg-white rounded-lg border p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4">3. Your Rights and Options</h2>
            <p class="text-gray-600 leading-relaxed mb-4">
              You have complete control over your data:
            </p>
            <ul class="space-y-2 text-gray-600">
              <li>• <strong>Adjust frequency:</strong> Change to weekly or monthly alerts</li>
              <li>• <strong>Unsubscribe:</strong> Stop receiving job alerts</li>
              <li>
                • <strong>Delete your data:</strong> Permanently remove all your information from our
                system
              </li>
            </ul>
            <p class="text-gray-600 leading-relaxed mt-4">
              All of these options are available through the preference link in any email from us.
            </p>
          </section>

          <section class="bg-white rounded-lg border p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4">4. Acceptable Use</h2>
            <p class="text-gray-600 leading-relaxed mb-4">
              When interacting with our service, you agree not to:
            </p>
            <ul class="space-y-2 text-gray-600">
              <li>• Attempt to gain unauthorized access to our systems</li>
              <li>• Interfere with or disrupt our service</li>
              <li>• Scrape, harvest, or collect data from our platform</li>
              <li>• Use our service to spam or harass others</li>
              <li>• Share your preference links with others (they are unique to you)</li>
            </ul>
          </section>

          <section class="bg-white rounded-lg border p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4">5. Job Listings and Third Parties</h2>
            <p class="text-gray-600 leading-relaxed mb-4">
              Talent<strong>Profile</strong> aggregates job listings from various sources. We:
            </p>
            <ul class="space-y-2 text-gray-600">
              <li>• Do not verify the accuracy of all job postings</li>
              <li>• Are not responsible for job listings or hiring decisions</li>
              <li>• Do not guarantee job offers or interviews</li>
              <li>• Are not liable for interactions with potential employers</li>
            </ul>
            <p class="text-gray-600 leading-relaxed mt-4">
              When you click a job link, you leave Talent<strong>Profile</strong> and are subject to the third-party
              website's terms and privacy policy.
            </p>
          </section>

          <section class="bg-white rounded-lg border p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
            <p class="text-gray-600 leading-relaxed">
              All content on Talent<strong>Profile</strong>, including our matching algorithm, design, and branding,
              is owned by Talent<strong>Profile</strong>. You may not copy, modify, distribute, or reverse engineer
              our technology.
            </p>
          </section>

          <section class="bg-white rounded-lg border p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4">7. Service Availability</h2>
            <p class="text-gray-600 leading-relaxed">
              We strive to provide consistent service but do not guarantee uninterrupted access. We
              may suspend the service for maintenance or updates. We are not liable for any service
              interruptions.
            </p>
          </section>

          <section class="bg-white rounded-lg border p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4">8. Termination</h2>
            <p class="text-gray-600 leading-relaxed mb-4">
              You may stop using Talent<strong>Profile</strong> at any time by:
            </p>
            <ul class="space-y-2 text-gray-600">
              <li>• Unsubscribing from emails</li>
              <li>• Deleting your data through the preferences page</li>
            </ul>
            <p class="text-gray-600 leading-relaxed mt-4">
              We may remove your data from our system if we determine our service is not a good fit
              for you, or if required by law.
            </p>
          </section>

          <section class="bg-white rounded-lg border p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4">9. Disclaimer of Warranties</h2>
            <p class="text-gray-600 leading-relaxed mb-4">
              Talent<strong>Profile</strong> is provided "as is" without warranties. We do not guarantee:
            </p>
            <ul class="space-y-2 text-gray-600">
              <li>• Error-free or uninterrupted service</li>
              <li>• Accurate or suitable job matches</li>
              <li>• Job offers or interviews</li>
              <li>• That job postings are current or available</li>
            </ul>
          </section>

          <section class="bg-white rounded-lg border p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <p class="text-gray-600 leading-relaxed">
              To the maximum extent permitted by law, Talent<strong>Profile</strong> shall not be liable for any
              indirect, incidental, special, or consequential damages arising from your use of our
              service.
            </p>
          </section>

          <section class="bg-white rounded-lg border p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
            <p class="text-gray-600 leading-relaxed">
              We may modify these Terms at any time. Material changes will be communicated via
              email. Continued receipt of our emails constitutes acceptance of updated terms.
            </p>
          </section>

          <section class="bg-white rounded-lg border p-8">
            <h2 class="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
            <p class="text-gray-600 leading-relaxed mb-4">Questions about these Terms?</p>
            <div class="space-y-2 text-gray-600">
              <p><strong>Email:</strong> {PUBLIC_EMAIL_LEGAL}</p>
              <p>
                <strong>Support:</strong>
                <a href="/contact" class="text-blue-600 hover:underline">Contact Form</a>
              </p>
            </div>
          </section>
        </div>
      </div>

      <!-- CTA -->
      <div
        class="mt-16 rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 p-12 text-center text-white"
      >
        <h2 class="text-2xl md:text-3xl font-bold mb-4">Ready to Get Started?</h2>
        <p class="mb-6 opacity-90">
          Join thousands of developers finding their dream jobs
        </p>
        <Button size="lg" variant="secondary" onclick={() => (window.location.href = '/')}>
          Sign Up Now
        </Button>
      </div>
    </div>
  </div>
</div>